const IOS_USER_AGENT_REGEX = /iPad|iPhone|iPod/

const isIosDevice = () => {
  if (typeof navigator === 'undefined') return false

  return (
    IOS_USER_AGENT_REGEX.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

const triggerDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.rel = 'noopener'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000)
}

const shareFileOnIos = async (blob: Blob, fileName: string) => {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.share !== 'function' ||
    typeof navigator.canShare !== 'function' ||
    typeof File === 'undefined' ||
    !isIosDevice()
  ) {
    return false
  }

  const file = new File([blob], fileName, {
    type: blob.type || 'application/octet-stream',
  })

  if (!navigator.canShare({ files: [file] })) {
    return false
  }

  await navigator.share({
    title: fileName,
    files: [file],
  })

  return true
}

export const inferFileNameFromUrl = (url: string, fallbackName: string) => {
  try {
    const pathname = new URL(url).pathname
    const lastSegment = pathname.split('/').pop()

    return lastSegment ? decodeURIComponent(lastSegment) : fallbackName
  } catch {
    return fallbackName
  }
}

export const downloadRemoteFile = async ({
  url,
  fileName,
  contentType,
}: {
  url: string
  fileName: string
  contentType?: string | null
}) => {
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`)
  }

  const fetchedBlob = await response.blob()
  const resolvedContentType = contentType || response.headers.get('content-type')
  const blob =
    resolvedContentType && fetchedBlob.type !== resolvedContentType
      ? new Blob([fetchedBlob], { type: resolvedContentType })
      : fetchedBlob

  const shared = await shareFileOnIos(blob, fileName)

  if (shared) {
    return 'shared'
  }

  triggerDownload(blob, fileName)

  return 'downloaded'
}
